import React from 'react';
import { withRouter } from 'react-router-dom';
import { Layout, Select, Popover, Alert, Button as AntButton } from 'antd';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  RightOutlined,
} from '@ant-design/icons';

import { clientsListService } from 'services/client';
import {
  usersSetLoginClientService,
  usersGetLoginClientService,
} from 'services/user';
import Auth from 'auth';
import { handleApiSuccess, handleApiError, accessControls } from 'utils';
import { setDocumentTitleFromPath } from 'utils/pageTitles';
import Footer from 'components/Footer';
import Logo from 'components/Logo';
// Temporarily disabled - AI search feature not working
// import SearchBox from 'components/SearchBox';
import { useNetworkSettings } from 'contexts/NetworkSettingsContext';
import NetworkCommanderMenu from 'components/menus/NetworkCommanderMenu';
import DefaultSuperAdminMenu from 'components/menus/DefaultSuperAdminMenu';

import { isMobile } from 'react-device-detect';

const _auth = Auth;
const { Header, Content, Sider } = Layout;

const { Option } = Select;

const RESELLER_PICKER_ROLES = [10, 11, 12, 13];
const LAST_RESELLER_CLIENT_KEY = 'msptrw-last-reseller-client';

function lastResellerClientStorageKey() {
  const payload = _auth.getPayload();
  return payload?.userId
    ? `${LAST_RESELLER_CLIENT_KEY}-${payload.userId}`
    : null;
}

function saveLastResellerClientId(clientId) {
  const key = lastResellerClientStorageKey();
  if (key && clientId) {
    localStorage.setItem(key, String(clientId));
  }
}

function getLastResellerClientId() {
  const key = lastResellerClientStorageKey();
  return key ? localStorage.getItem(key) : null;
}

function needsResellerPicker() {
  const role = Number(_auth.getRole());
  return RESELLER_PICKER_ROLES.includes(role);
}

/**
 * Shown while a super admin is impersonating another user. Reads the
 * `impersonatedBy` claim from the active token and offers a one-click way to
 * restore the admin's own session (stashed at impersonation time).
 */
const ImpersonationBanner = () => {
  const adminToken = localStorage.getItem('impersonation_admin_token');
  const payload = _auth.getPayload();
  if (!adminToken || !payload || !payload.impersonatedBy) {
    return null;
  }

  const returnToAdmin = () => {
    localStorage.removeItem('impersonation_admin_token');
    _auth.authenticate(adminToken, function () {
      window.location.href = '/';
    });
  };

  return (
    <Alert
      type="warning"
      banner
      showIcon
      style={{ marginBottom: 16 }}
      message={
        <span>
          You are impersonating <b>{payload.email || 'a user'}</b> for
          debugging.
        </span>
      }
      action={
        <AntButton size="small" danger onClick={returnToAdmin}>
          Return to my account
        </AntButton>
      }
    />
  );
};

const ControlPanelLayoutWrapper = (props) => {
  const { networkSettings, loading } = useNetworkSettings();

  return (
    <ControlPanelLayout
      {...props}
      networkSettings={networkSettings}
      networkSettingsLoading={loading}
    />
  );
};

const HeaderLayout = ({
  collapsed,
  clients,
  activeClient,
  toggle,
  clientChanged,
}) => {
  return (
    <Header
      className="site-layout-background"
      style={{
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        height: '64px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {React.createElement(
          collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
          {
            className: 'trigger',
            onClick: toggle,
          },
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          verticalAlign: 'middle',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        {(() => {
          return clients && clients.length > 1;
        })() && (
          <div style={{ display: 'inline' }}>
            <span className="clientLabel">
              <span style={{ letterSpacing: 2 }}>RESELLER</span> &nbsp;&nbsp;
              <RightOutlined style={{ color: '#EAEAEA' }} />
            </span>
            <Popover
              content={<p>Please select client to load associated data</p>}
              title="Select Reseller"
              visible={activeClient._id ? false : true}
            >
              <Select
                showSearch
                placeholder="Select a client"
                optionFilterProp="children"
                className="selectClient"
                onChange={clientChanged}
                defaultValue={activeClient._id}
                value={activeClient._id}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                style={{ minWidth: 150, maxWidth: 200 }}
              >
                {clients.map((item, index) => (
                  <Option key={index} value={item._id}>
                    {item.client_name}
                  </Option>
                ))}
              </Select>
            </Popover>
          </div>
        )}
        {/* Temporarily hidden - AI search feature */}
        {/* {clients && clients.length > 0 && (
          <SearchBox
            style={{
              display: 'flex',
              verticalAlign: 'middle',
              marginLeft: 20,
            }}
          />
        )} */}
      </div>
    </Header>
  );
};

class ControlPanelLayout extends React.Component {
  state = {
    permissions: [],
    clients: [],
    collapsed: isMobile ? true : false,
    activeClient: {},
    dynamicIcons: [],
  };

  async componentDidMount() {
    setDocumentTitleFromPath(this.props.location.pathname);
    console.log('ControlPanelLayout: componentDidMount - Loading clients...');

    Promise.all([clientsListService(), usersGetLoginClientService()])
      .then(([clientsResponse, activeClientResponse]) => {
        const clients =
          clientsResponse.data.items && clientsResponse.data.items.length > 0
            ? clientsResponse.data.items
            : [];
        const activeClient =
          activeClientResponse.data.items &&
          activeClientResponse.data.items.length > 0
            ? activeClientResponse.data.items[0]
            : {};

        console.log('ControlPanelLayout: Clients count:', clients.length);
        console.log('ControlPanelLayout: Active client:', activeClient);

        this.setState({ clients, activeClient }, () => {
          this.maybeRestoreLastResellerClient(clients, activeClient);
        });
      })
      .catch((error) => {
        console.error('ControlPanelLayout: Error loading client context:', error);
        this.setState({ clients: [], activeClient: {} });
      });

    //********START  ACCESS CONTROL******** */
    accessControls.fetch().then((permissions) => {
      console.log(permissions);
      this.setState({ permissions });
    });
    //********END ACCESS CONTROL******** */
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      setDocumentTitleFromPath(this.props.location.pathname);
    }
  }

  isNetworkingLayout = (pathname) => {
    const netowrkPagesArr = [
      '/countries',
      '/network-dashboard',
      '/states',
      '/bandwidth',
      '/print',
      '/equipmenttype',
      '/equipment-vendor',
      '/equipment-model',
      '/qosoptions',
      '/branchnetwork',
      '/branchcustomer',
      '/devicelog',
      '/expansiveworkspacelog',
      '/dashboardlog',
      '/dashboardlogactivity',
      '/vlanregistry',
      '/deviceregistry',
      '/officenumber',
      '/portregistry',
      '/ipportregistry',
      '/officeregistry',
      '/wifigroup',
      '/wifiusers',
      '/equipmentownership',
      '/clientfirewall',
      '/firewallports',
      '/map-ip-address-to-client',
    ];
    for (let i = 0; i < netowrkPagesArr.length; i++) {
      if (pathname.startsWith(netowrkPagesArr[i])) {
        return true;
      }
    }
    return false;
  };

  onCollapse = (collapsed) => {
    this.setState({ collapsed });
  };

  toggle = () => {
    this.setState({
      collapsed: !this.state.collapsed,
    });
  };

  maybeRestoreLastResellerClient = (clients, activeClient) => {
    if (
      !needsResellerPicker() ||
      !clients?.length ||
      this._restoringResellerClient
    ) {
      return;
    }

    const lastClientId = getLastResellerClientId();
    if (!lastClientId) {
      return;
    }

    const currentClientId = activeClient?._id ? String(activeClient._id) : '';
    if (currentClientId === String(lastClientId)) {
      return;
    }

    const isValidClient = clients.some(
      (client) => String(client._id) === String(lastClientId),
    );
    if (!isValidClient) {
      return;
    }

    this._restoringResellerClient = true;
    this.clientChanged(lastClientId, { skipSave: true });
  };

  clientChanged = (value, options = {}) => {
    console.log('ControlPanelLayout: Client changed to:', value);

    usersSetLoginClientService(value)
      .then((response) => {
        console.log(
          'ControlPanelLayout: Client switch successful:',
          response.data,
        );
        _auth.authenticate(response.data.token, function () {
          if (!options.skipSave) {
            saveLastResellerClientId(value);
          }
          handleApiSuccess(response);
          setTimeout(() => {
            window.location.href = process.env.REACT_APP_APP_BASE_URL;
          }, 1000);
        });
      })
      .catch((error) => {
        console.error('ControlPanelLayout: Error switching client:', error);
        handleApiError(error);
      });
  };

  menuClickHandle = () => {
    if (isMobile) {
      this.toggle();
    }
    // On menu change clear local storage item
    let localStoredSearchQuery = localStorage.getItem('msptrw-kbSearchQuery');
    if (localStoredSearchQuery) {
      localStorage.removeItem('msptrw-kbSearchQuery');
    }
  };

  render() {
    const { pathname } = this.props.location;
    const { networkSettings = {} } = this.props;
    let defaultOpened = [];
    // Find the best matching menu key for the current pathname
    // We'll use all possible keys from static and dynamic menus
    const allMenuKeys = [
      '/dashboard',
      '/clients',
      '/apikeys',
      '/access-control',
      '/companies',
      '/yardi-sync',
      '/branches',
      '/users',
      '/users/super-admins',
      '/users/legal-acceptance',
      '/third-party-credentials',
      '/call-report',
      '/manage-isp-provider',
      '/isp',
      '/utility-providers',
      '/service-providers',
      '/admincontrol',
      '/network-dashboard',
      '/branchnetwork',
      '/branchcustomer',
      '/vlanregistry',
      '/deviceregistry',
      '/ipregistry',
      '/officenumber',
      '/portregistry',
      '/officeregistry',
      '/ipportregistry',
      '/wifigroup',
      '/wifiusers',
      '/wifiusers/wifiusers-super-admin',
      '/equipmenttype',
      '/equipment-vendor',
      '/equipment-model',
      '/equipmentownership',
      '/clientfirewall',
      '/firewallports',
      '/devicelog',
      '/dashboardlog',
      '/expansive-api-data',
      '/email-send-log',
      '/expect-scripts',
      '/raddb-reports',
      '/map-ip-address-to-client',
      '/map-port-to-client',
      '/rackdevice',
      '/rackregistry',
      '/gallery',
      '/gallery/create',
      '/knowledge-base',
      '/knowledge-base/categories',
      '/knowledge-base/view',
      '/manageKB',
      '/billing',
      '/workspace',
      '/sales_person',
      '/website',
      '/reports',
      '/countries',
      '/states',
      '/bandwidth',
      '/qosoptions',
      '/time-tracker',
      '/time-tracker/projects',
      '/time-tracker/projects/view',
      '/time-tracker/user-groups',
      '/time-tracker/tracker',
      '/time-tracker/reports',
      '/time-tracker/open-timers',
      '/billing',
      '/billing/dashboard',
      '/billing/invoices',
      '/billing/recurring',
      '/billing/payments',
      '/billing/items',
      '/billing/categories',
      '/billing/tax-rates',
      '/billing/settings',
      '/tickets',
      '/tickets/dashboard',
      '/tickets/projects',
      '/tickets/all',
      '/tickets/view',
      // Add more as needed
    ];
    // Find the longest matching key
    let defaultSelected = allMenuKeys.reduce((best, key) => {
      if (
        pathname === key ||
        (pathname.startsWith(key) && key.length > best.length)
      ) {
        return key;
      }
      return best;
    }, '/dashboard');

    // Set default opened menus based on current path
    if (
      pathname.startsWith('/branchnetwork') ||
      pathname.startsWith('/branchcustomer') ||
      pathname.startsWith('/wifiusers') ||
      pathname.startsWith('/officeregistry') ||
      pathname.startsWith('/portregistry') ||
      pathname.startsWith('/map-ip-address-to-client') ||
      pathname.startsWith('/map-port-to-client') ||
      pathname.startsWith('/rackregistry')
    ) {
      defaultOpened = ['/network-management'];
    } else if (
      pathname.startsWith('/deviceregistry') ||
      pathname.startsWith('/raddb-reports') ||
      pathname.startsWith('/wifiusers/wifiusers-super-admin') ||
      pathname.startsWith('/rackdevice') ||
      pathname.startsWith('/officenumber')
    ) {
      defaultOpened = ['/tech-tools'];
    } else if (
      pathname.startsWith('/devicelog') ||
      pathname.startsWith('/expect-scripts') ||
      pathname.startsWith('/dashboardlog') ||
      pathname.startsWith('/expansive-api-data') ||
      pathname.startsWith('/email-send-log')
    ) {
      defaultOpened = ['/monitoring-logs'];
    } else if (
      pathname.startsWith('/ipportregistry') ||
      pathname.startsWith('/clientfirewall') ||
      pathname.startsWith('/firewallports') ||
      pathname.startsWith('/reports') ||
      pathname.startsWith('/equipmenttype') ||
      pathname.startsWith('/equipment-vendor') ||
      pathname.startsWith('/equipment-model') ||
      pathname.startsWith('/equipmentownership') ||
      pathname.startsWith('/countries') ||
      pathname.startsWith('/states') ||
      pathname.startsWith('/bandwidth') ||
      pathname.startsWith('/qosoptions')
    ) {
      defaultOpened = ['/config-admin'];
    } else if (
      pathname.startsWith('/knowledge-base') ||
      pathname.startsWith('/gallery')
    ) {
      defaultOpened = ['/kb'];
    } else if (pathname.startsWith('/website')) {
      defaultOpened = ['/website'];
    } else if (
      pathname.startsWith('/time-tracker/projects') ||
      pathname.startsWith('/time-tracker/user-groups') ||
      pathname.startsWith('/time-tracker/tracker') ||
      pathname.startsWith('/time-tracker/reports') ||
      pathname.startsWith('/time-tracker/open-timers')
    ) {
      defaultOpened = ['/time-tracker'];
    } else if (pathname.startsWith('/billing')) {
      defaultOpened = ['/billing'];
    } else if (pathname.startsWith('/tickets')) {
      defaultOpened = ['/tickets'];
    } else if (
      pathname.startsWith('/apikeys') ||
      pathname.startsWith('/access-control') ||
      pathname.startsWith('/companies') ||
      pathname.startsWith('/clients') ||
      pathname.startsWith('/yardi-sync') ||
      pathname.startsWith('/branches') ||
      pathname.startsWith('/branchnetwork') ||
      pathname.startsWith('/users') ||
      pathname.startsWith('/third-party-credentials') ||
      pathname.startsWith('/call-report') ||
      pathname.startsWith('/manage-isp-provider') ||
      pathname.startsWith('/super-admin')
    ) {
      defaultOpened = ['/super-admin'];
    }

    // NETWORK COMMANDER MENU
    if (
      process.env.REACT_APP_THEME === 'NETWORK_COMMANDER' ||
      process.env.REACT_APP_THEME === 'WORKSPACEGEEK' ||
      process.env.REACT_APP_THEME === '640TECH'
    ) {
      return (
        <Layout style={{ minHeight: '100vh' }}>
          <Sider
            width={250}
            collapsedWidth={isMobile ? 0 : 80}
            trigger={null}
            collapsible
            collapsed={this.state.collapsed}
            onCollapse={this.onCollapse}
            style={{
              overflow: 'auto',
              height: '100vh',
              position: 'fixed',
              zIndex: 99999,
              width: '250px',
              backgroundColor:
                networkSettings.navbar_current_mode === 'dark'
                  ? networkSettings.navbar_color_dark_mode
                  : networkSettings.navbar_color_light_mode,
            }}
          >
            {!networkSettings.hide_logo && (
              <Logo navbar_current_mode={networkSettings.navbar_current_mode} />
            )}
            <NetworkCommanderMenu
              permissions={this.state.permissions}
              networkSettings={networkSettings}
              defaultSelected={defaultSelected}
              defaultOpened={defaultOpened}
              menuClickHandle={this.menuClickHandle}
            />
          </Sider>
          <Layout
            className="site-layout"
            style={
              this.state.collapsed
                ? isMobile
                  ? {}
                  : { marginLeft: 80 }
                : { marginLeft: 250 }
            }
          >
            <HeaderLayout
              collapsed={this.state.collapsed}
              clients={this.state.clients}
              activeClient={this.state.activeClient}
              toggle={this.toggle}
              clientChanged={this.clientChanged}
            />

            <Content style={{ margin: '24px 16px 0 16px' }}>
              <ImpersonationBanner />
              {this.props.children}
            </Content>
            <Footer />
          </Layout>
        </Layout>
      );
    } else {
      // DEFAULT SUPER ADMIN MENU
      return (
        <Layout style={{ minHeight: '100vh' }}>
          <Sider
            width={250}
            collapsedWidth={isMobile ? 0 : 80}
            trigger={null}
            collapsible
            collapsed={this.state.collapsed}
            onCollapse={this.onCollapse}
            style={{
              overflow: 'auto',
              height: '100vh',
              position: 'fixed',
              zIndex: 99999,
              width: '250px',
              backgroundColor:
                networkSettings.navbar_current_mode === 'dark'
                  ? networkSettings.navbar_color_dark_mode
                  : networkSettings.navbar_color_light_mode,
            }}
          >
            {!networkSettings.hide_logo && (
              <Logo navbar_current_mode={networkSettings.navbar_current_mode} />
            )}
            <DefaultSuperAdminMenu
              permissions={this.state.permissions}
              networkSettings={networkSettings}
              defaultSelected={defaultSelected}
              defaultOpened={defaultOpened}
              menuClickHandle={this.menuClickHandle}
            />
          </Sider>
          <Layout
            className="site-layout"
            style={
              this.state.collapsed
                ? isMobile
                  ? {}
                  : { marginLeft: 80 }
                : { marginLeft: 250 }
            }
          >
            <HeaderLayout
              collapsed={this.state.collapsed}
              clients={this.state.clients}
              activeClient={this.state.activeClient}
              toggle={this.toggle}
              clientChanged={this.clientChanged}
            />

            <Content style={{ margin: '24px 16px 0 16px' }}>
              <ImpersonationBanner />
              {this.props.children}
            </Content>
            <Footer />
          </Layout>
        </Layout>
      );
    }
  }
}

export default withRouter(ControlPanelLayoutWrapper);
